// useScrollLoad.ts
import { ref, onMounted, onUnmounted } from 'vue'

interface ScrollLoadOptions<T> {
  initPage?: number
  pageSize?: number
  requestData: (params: {
    page: number
    pageSize: number
    [key: string]: any
  }) => Promise<{
    code: number
    data: {
      data: []
      total: number
    }
  }>
  searchParams?: () => Record<string, any>
  autoLoad?: boolean
}

export function useScrollLoad<T = any>(options: ScrollLoadOptions<T>) {
  const {
    initPage = 1,
    pageSize = 10,
    requestData,
    searchParams = () => ({}),
    autoLoad = true
  } = options

  const currentPage = ref(initPage)
  const hasMore = ref(true)
  const loading = ref(false)
  const list = ref<T[]>([])
  const total = ref(0)

  const loadData = async () => {
    if (!hasMore.value || loading.value) return

    loading.value = true
    try {
      const res = await requestData({
        page: currentPage.value,
        pageSize,
        ...searchParams()
      })

      if (res.code === 0) {
        const { data: newData, total: totalCount } = res.data

        if (currentPage.value === 1) {
          list.value = newData
        } else {
          list.value.push(...newData)
        }

        total.value = totalCount
        hasMore.value = list.value.length < totalCount
        currentPage.value++
      }
    } catch (error) {
      console.error('Load data error:', error)
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    currentPage.value = initPage
    hasMore.value = true
    list.value = []
    total.value = 0
    loadData()
  }

  onMounted(() => {
    if (autoLoad) {
      loadData()
    }
  })

  return {
    list,
    loading,
    hasMore,
    total,
    reset,
    loadData
  }
}
